import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import {
  Briefcase,
  Building2,
  CalendarDays,
  FolderOpen,
  Home,
  ShieldAlert,
  Users,
  Wrench,
  X,
} from "lucide-react";

const relationsSubMenu = [
  { text: "Leads", path: "/prospects/leads" },
  { text: "Prospects", path: "/prospects/prospects" },
  { text: "Familles", path: "/prospects/familles" },
  { text: "Sociétés", path: "/prospects/societes" },
];

const conseilSubMenu = [
  { text: "Audits patrimoniaux", path: "/conseil/audits" },
  { text: "Allocations financières", path: "/conseil/allocations" },
];

const transactionsSubMenu = [
  { text: "Opérations", path: "/transactions" },
  { text: "Produits financiers", path: "/produits" },
  { text: "Partenaires", path: "/partenaires" },
];

const organisationSubMenu = [
  { text: "Calendrier", path: "/calendrier" },
  { text: "Tâches", path: "/tasks" },
  { text: "Documentation", path: "/documents" },
];

const toolsSubMenu = [
  { text: "Quantalys", path: "/outils/quantalys" },
  { text: "Simulateur Allocation", path: "/outils/allocation" },
  { text: "Simulateur Impôts", path: "/outils/impots" },
  { text: "Mailing", path: "/outils/brevo" },
];

const adminSubMenu = [
  { text: "Sécurité & Audit", path: "/parametres/audit" },
  { text: "Permissions", path: "/parametres/permissions" },
];

function isRouteActive(path, location) {
  const [pathname, query] = path.split("?");

  if (query) {
    return location.pathname === pathname && location.search === `?${query}`;
  }

  return location.pathname === pathname || location.pathname.startsWith(`${pathname}/`);
}

function SidebarSubMenu({
  id,
  icon: Icon,
  label,
  items,
  isOpen,
  onToggle,
  onNavigate,
  location,
  reduceMotion,
}) {
  const hasActiveItem = items.some(({ path }) => isRouteActive(path, location));
  const transition = reduceMotion ? { duration: 0 } : { duration: 0.22, ease: "easeOut" };

  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={id}
        className={`flex w-full items-center rounded-md px-4 py-2.5 text-left transition-colors focus-hp ${
          isOpen || hasActiveItem
            ? "bg-primary-light font-semibold text-primary"
            : "text-primary hover:bg-surface-muted"
        }`}
      >
        <Icon aria-hidden="true" className="size-[25px] shrink-0" />
        <span className="ml-3 min-w-0 flex-1 truncate text-[17.5px]">{label}</span>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={id}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={transition}
            className="overflow-hidden"
          >
            <ul className="ml-5 mt-1 space-y-1 border-l border-tertiary py-1 pl-3">
              {items.map(({ text, path }) => {
                const isActive = isRouteActive(path, location);

                return (
                  <li key={path}>
                    <Link
                      to={path}
                      onClick={onNavigate}
                      aria-current={isActive ? "page" : undefined}
                      className={`block rounded-md px-3 py-2 text-sm transition-colors focus-hp ${
                        isActive
                          ? "bg-primary-light font-semibold text-primary"
                          : "text-muted hover:bg-surface-muted hover:text-primary"
                      }`}
                    >
                      {text}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}

export default function Sidebar({ user, onClose }) {
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const [openRelations, setOpenRelations] = useState(false);
  const [openConseil, setOpenConseil] = useState(false);
  const [openTransactions, setOpenTransactions] = useState(false);
  const [openOrganisation, setOpenOrganisation] = useState(false);
  const [openTools, setOpenTools] = useState(false);
  const [openAdmin, setOpenAdmin] = useState(false);

  useEffect(() => {
    setOpenRelations(relationsSubMenu.some(({ path }) => isRouteActive(path, location)));
    setOpenConseil(conseilSubMenu.some(({ path }) => isRouteActive(path, location)));
    setOpenTransactions(transactionsSubMenu.some(({ path }) => isRouteActive(path, location)));
    setOpenOrganisation(organisationSubMenu.some(({ path }) => isRouteActive(path, location)));
    setOpenTools(toolsSubMenu.some(({ path }) => isRouteActive(path, location)));
    setOpenAdmin(adminSubMenu.some(({ path }) => isRouteActive(path, location)));
  }, [location]);

  const primaryLinkClass = (isActive) =>
    `flex items-center rounded-md px-4 py-3 text-[17.5px] transition-colors focus-hp ${
      isActive
        ? "bg-primary-light font-semibold text-primary"
        : "text-primary hover:bg-surface-muted"
    }`;

  return (
    <aside
      id="workspace-sidebar"
      aria-label="Navigation principale"
      className="relative flex h-full w-64 flex-col overflow-hidden rounded-br-[120px] border-r border-tertiary bg-surface text-primary"
    >
      <div className="relative flex shrink-0 items-center justify-center px-4 py-3">
        <img src="/assets/logo-title.png" alt="Hipparchus" className="h-[120px] w-auto" />
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer le menu latéral"
            className="absolute right-3 top-3 rounded-md p-2 text-muted transition-colors hover:bg-surface-muted hover:text-primary focus-hp"
          >
            <X aria-hidden="true" className="size-5" />
          </button>
        )}
      </div>

      <nav className="custom-scrollbar min-h-0 flex-1 overflow-x-hidden overflow-y-auto pb-28">
        <ul className="space-y-1 px-2">
          <li>
            <Link
              to="/dashboard"
              onClick={onClose}
              aria-current={location.pathname === "/dashboard" ? "page" : undefined}
              className={primaryLinkClass(location.pathname === "/dashboard")}
            >
              <Home aria-hidden="true" className="size-[25px] shrink-0" />
              <span className="ml-3">Dashboard</span>
            </Link>
          </li>

          <li>
            <Link
              to="/forum"
              onClick={onClose}
              aria-current={location.pathname.startsWith("/forum") ? "page" : undefined}
              className={primaryLinkClass(location.pathname.startsWith("/forum"))}
            >
              <Building2 aria-hidden="true" className="size-[25px] shrink-0" />
              <span className="ml-3">Forum</span>
            </Link>
          </li>

          <SidebarSubMenu
            id="sidebar-clients"
            icon={Users}
            label="Clients"
            items={relationsSubMenu}
            isOpen={openRelations}
            onToggle={() => setOpenRelations((open) => !open)}
            onNavigate={onClose}
            location={location}
            reduceMotion={reduceMotion}
          />

          <SidebarSubMenu
            id="sidebar-conseil"
            icon={Briefcase}
            label="Conseil"
            items={conseilSubMenu}
            isOpen={openConseil}
            onToggle={() => setOpenConseil((open) => !open)}
            onNavigate={onClose}
            location={location}
            reduceMotion={reduceMotion}
          />

          <SidebarSubMenu
            id="sidebar-transactions"
            icon={FolderOpen}
            label="Transactions"
            items={transactionsSubMenu}
            isOpen={openTransactions}
            onToggle={() => setOpenTransactions((open) => !open)}
            onNavigate={onClose}
            location={location}
            reduceMotion={reduceMotion}
          />

          <SidebarSubMenu
            id="sidebar-organisation"
            icon={CalendarDays}
            label="Organisation"
            items={organisationSubMenu}
            isOpen={openOrganisation}
            onToggle={() => setOpenOrganisation((open) => !open)}
            onNavigate={onClose}
            location={location}
            reduceMotion={reduceMotion}
          />

          <SidebarSubMenu
            id="sidebar-tools"
            icon={Wrench}
            label="Outils"
            items={toolsSubMenu}
            isOpen={openTools}
            onToggle={() => setOpenTools((open) => !open)}
            onNavigate={onClose}
            location={location}
            reduceMotion={reduceMotion}
          />

          {user?.Role === "Administrateur" && (
            <SidebarSubMenu
              id="sidebar-admin"
              icon={ShieldAlert}
              label="Administration"
              items={adminSubMenu}
              isOpen={openAdmin}
              onToggle={() => setOpenAdmin((open) => !open)}
              onNavigate={onClose}
              location={location}
              reduceMotion={reduceMotion}
            />
          )}
        </ul>
      </nav>

      <div className="absolute bottom-0 w-full rounded-br-[120px] bg-surface p-8 text-center shadow-hp-md">
        <Link
          to="/support"
          onClick={onClose}
          className="flex items-center justify-start rounded-md text-primary transition-colors hover:text-muted focus-hp"
        >
          <Wrench aria-hidden="true" className="mr-2 size-5 text-muted" />
          <span className="text-sm font-bold">SUPPORT</span>
        </Link>
      </div>
    </aside>
  );
}
